import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatAppDate, formatAppDateTime, parseDbDateTime } from "../utils/appDateTime";
import { FileText, Search } from "lucide-react";
import { FaSlidersH } from "react-icons/fa";
import { AtlasRefreshIconButton } from "./list-toolbar";
import axios from "axios";
import API from "../api";
import StaffAvatarCircle from "./StaffAvatarCircle";
import DateInput from "./DateInput";
import { AtlasListToolbar } from "./list-toolbar";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  AdminAuditsFilterFields,
  buildAdminAuditsFilterChips,
} from "./filters";
import { EmbeddedFilterPanel } from "./embedded-filter";
import { EMBEDDED_FILTER_LABEL_CLASS, EMBEDDED_FILTER_SELECT_CLASS } from "./embedded-filter/constants";

const AUDIT_SORT_OPTIONS = [{ value: "created", label: "Created" }];

const SUBJECT_TYPE_OPTIONS = [
  { label: "All", value: "" },
  { label: "Account", value: "account" },
  { label: "Field Staff", value: "fieldstaff" },
  { label: "Office Staff", value: "officestaff" },
  { label: "Candidate", value: "candidate" },
  { label: "Outsourced Staff", value: "outsourcedstaff" },
  { label: "Contact", value: "contact" },
  { label: "Client", value: "client" },
  { label: "Client Location", value: "client_location" },
  { label: "Client Account", value: "client_account" },
  { label: "Office", value: "office" },
  { label: "Shift", value: "shift" },
  { label: "Shift Actual", value: "shift_actual" },
  { label: "Week", value: "week" },
  { label: "Adjustment", value: "adjustment" },
  { label: "Payer", value: "payer" },
  { label: "Preference", value: "preference" },
  { label: "Employee Certification", value: "employee_cert" },
  { label: "Address", value: "address" },
  { label: "File", value: "file" },
  { label: "Tag", value: "tag" },
  { label: "Type", value: "type" },
  { label: "Status", value: "status" },
];

const DETAIL_TYPE_OPTIONS = [...SUBJECT_TYPE_OPTIONS];
const ACTION_OPTIONS = [
  { label: "All", value: "" },
  { label: "Create", value: "create" },
  { label: "Add", value: "add" },
  { label: "Change", value: "change" },
  { label: "Remove", value: "remove" },
  { label: "Delete", value: "delete" },
  { label: "Info", value: "info" },
];

const LIMIT_OPTIONS = [10, 25, 50, 100];
const SEARCH_DEBOUNCE_MS = 500;
// Must match AUDIT_SEARCH_MIN_LENGTH in the backend's routes/auditsRoutes.js.
const SEARCH_MIN_LENGTH = 3;

/** Map rich audit `actorAccount` to the shape expected by {@link StaffAvatarCircle}. */
function actorEmployeeFromAuditActor(actor, actorName) {
  const rawId = actor?.id ?? actor?.account_id ?? actor?.accountId;
  const id = rawId != null && rawId !== "" ? Number(rawId) : NaN;
  const emp = {
    first_name: actor?.firstName,
    last_name: actor?.lastName,
    name: actorName || actor?.name || "Unknown",
  };
  if (Number.isFinite(id) && id > 0) {
    emp.account_id = id;
  }
  if (actor?.avatar) {
    emp.avatar = actor.avatar;
  }
  if (actor?.email) {
    emp.email = actor.email;
  }
  return emp;
}

const RichAuditsPanel = ({
  subjectId,
  fetchUrl,
  title = "Audit History",
  messageMode = false,
  /** When true, load audits from fetchUrl without a scoped subjectId (e.g. GET /api/audits/rich). */
  globalList = false,
  /** Flatten outer card/title for embedding inside another admin shell (e.g. Admin Audits page). */
  embedded = false,
}) => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanel = useFilterPanel();
  const [searchTerm, setSearchTerm] = useState("");
  // Search is sent only after typing pauses, and (global list) only with 3+ characters:
  // the global audit search is expensive, and firing it per keystroke took the whole
  // system down on 2026-09-24.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const trimmedSearch = searchTerm.trim();
  const searchTooShort =
    globalList && trimmedSearch.length > 0 && trimmedSearch.length < SEARCH_MIN_LENGTH;
  const [searchTruncated, setSearchTruncated] = useState(false);
  const requestRef = useRef(null);
  const [subjectObjectClass, setSubjectObjectClass] = useState("");
  const [auditMetaObjectClass, setAuditMetaObjectClass] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortBy] = useState("created");
  const [showObjectId, setShowObjectId] = useState(false);
  const [showLimitMenu, setShowLimitMenu] = useState(false);

  const limitMenuRef = useRef(null);
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const offset = (page - 1) * limit;
  const fromRow = total === 0 ? 0 : offset + 1;
  const toRow = Math.min(offset + limit, total);

  const actionLabel = (value) => {
    const v = String(value || "").toLowerCase();
    if (!v) return "Updated";
    if (v === "create") return "Created";
    if (v === "change") return "Changed";
    if (v === "update") return "Updated";
    if (v === "delete") return "Deleted";
    if (v === "signin") return "Signed In";
    return v.charAt(0).toUpperCase() + v.slice(1);
  };

  const formatShortDate = (value) => {
    if (!value) return "";
    const date = parseDbDateTime(value);
    if (!date) return "";
    return formatAppDate(date, { emptyValue: "" });
  };

  const formatRelativeTime = (value) => {
    if (!value) return "";
    const date = parseDbDateTime(value);
    if (!date) return "";
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return formatShortDate(value);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${mins % 60}m ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return formatShortDate(value);
  };

  const parseMessageHeader = (message) => {
    const firstLine = String(message || "").split("\n")[0] || "";
    const m = firstLine.match(
      /^(Created|Changed|Updated|Deleted)\s+Adjustment:\s*(.*?)\s*\((\d+)\)\s*$/i,
    );
    if (!m) {
      return {
        title: "Adjustment",
        subtitle: firstLine || "No details available.",
      };
    }
    return {
      title: "Adjustment",
      subtitle: `${m[2]} (${m[3]})`,
    };
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") return "-";
    const raw = String(value).trim();
    if (raw.toLowerCase() === "null") return "-";
    return raw;
  };

  // Values the backend explicitly typed 'datetime' are stored as UTC ISO timestamps
  // (e.g. "2026-07-17T19:13:43+00:00"); show them in the app's local date/time instead.
  // Anything else -- or a datetime that doesn't parse, or a placeholder year like 0001 --
  // is shown exactly as before.
  const formatMetaValue = (meta) => {
    const fallback = formatValue(meta?.value);
    if (fallback === "-" || meta?.valueType !== "datetime") return fallback;
    const date = parseDbDateTime(fallback);
    if (!date || date.getUTCFullYear() < 1900) return fallback;
    return formatAppDateTime(date, { emptyValue: fallback });
  };

  const groupMetas = (audit) => {
    const metas = Array.isArray(audit?.auditMetas) ? audit.auditMetas : [];
    const grouped = new Map();
    metas.forEach((meta) => {
      const objectClassName = meta.objectClassName || meta.objectClass || "Record";
      const objectId = meta.objectId || "";
      const objectName = meta.objectName || "";
      const key = `${objectClassName}::${objectId}::${objectName}`;
      const list = grouped.get(key) || {
        key,
        objectClassName,
        objectId,
        objectName,
        items: [],
      };
      list.items.push(meta);
      grouped.set(key, list);
    });
    return Array.from(grouped.values());
  };

  const loadAudits = useCallback(async () => {
    if (!fetchUrl) return;
    if (
      !globalList &&
      (subjectId === undefined ||
        subjectId === null ||
        String(subjectId).trim() === "")
    ) {
      return;
    }
    // Cancel the previous request so an outdated search stops on the server too.
    if (requestRef.current) requestRef.current.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const params = {
        offset,
        limit,
        order: `date,${sortDirection}`,
        search: debouncedSearch || undefined,
        subjectObjectClass: subjectObjectClass || undefined,
        auditMetaObjectClass: auditMetaObjectClass || undefined,
        action: filterAction || undefined,
        dateFrom: messageMode ? undefined : filterDateFrom || undefined,
        dateTo: messageMode ? undefined : filterDateTo || undefined,
        showObjectId: showObjectId ? "true" : "false",
      };

      const response = await API.get(fetchUrl, { params, signal: controller.signal });
      if (requestRef.current !== controller) return;
      const payload = response?.data?.data || {};
      const paging = payload.paging || {};
      setAudits(Array.isArray(payload.audits) ? payload.audits : []);
      setTotal(Number(paging.records || 0));
      setSearchTruncated(Boolean(payload.searchTruncated));
    } catch (err) {
      if (axios.isCancel(err) || requestRef.current !== controller) return;
      console.error("Failed to fetch rich audits:", err);
      setAudits([]);
      setTotal(0);
      setSearchTruncated(false);
      setError(err?.response?.data?.message || "Failed to load audits");
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  }, [
    fetchUrl,
    globalList,
    subjectId,
    offset,
    limit,
    sortDirection,
    debouncedSearch,
    subjectObjectClass,
    auditMetaObjectClass,
    filterAction,
    filterDateFrom,
    filterDateTo,
    showObjectId,
    messageMode,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    subjectId,
    globalList,
    limit,
    sortDirection,
    subjectObjectClass,
    auditMetaObjectClass,
    filterAction,
    filterDateFrom,
    filterDateTo,
    showObjectId,
    debouncedSearch,
    fetchUrl,
  ]);

  useEffect(() => {
    if (searchTooShort) return undefined;
    const timer = setTimeout(
      () => setDebouncedSearch(trimmedSearch),
      trimmedSearch ? SEARCH_DEBOUNCE_MS : 0,
    );
    return () => clearTimeout(timer);
  }, [trimmedSearch, searchTooShort]);

  useEffect(() => {
    loadAudits();
  }, [loadAudits]);

  useEffect(
    () => () => {
      if (requestRef.current) requestRef.current.abort();
    },
    [],
  );

  useEffect(() => {
    const onDocClick = (event) => {
      if (showLimitMenu && limitMenuRef.current && !limitMenuRef.current.contains(event.target)) {
        setShowLimitMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showLimitMenu]);

  const activeFilterChips = [
    subjectObjectClass
      ? {
        key: "subjectObjectClass",
        label: `Subject: ${SUBJECT_TYPE_OPTIONS.find((opt) => opt.value === subjectObjectClass)?.label || subjectObjectClass}`,
      }
      : null,
    auditMetaObjectClass
      ? {
        key: "auditMetaObjectClass",
        label: `Detail: ${DETAIL_TYPE_OPTIONS.find((opt) => opt.value === auditMetaObjectClass)?.label || auditMetaObjectClass}`,
      }
      : null,
    filterAction
      ? {
        key: "filterAction",
        label: `Action: ${ACTION_OPTIONS.find((opt) => opt.value === filterAction)?.label || filterAction}`,
      }
      : null,
    !messageMode && filterDateFrom
      ? { key: "filterDateFrom", label: `From: ${filterDateFrom}` }
      : null,
    !messageMode && filterDateTo
      ? { key: "filterDateTo", label: `To: ${filterDateTo}` }
      : null,
    showObjectId ? { key: "showObjectId", label: "Show Object ID" } : null,
    sortDirection !== "desc"
      ? { key: "sortDirection", label: `Direction: ${sortDirection === "asc" ? "Ascending" : "Descending"}` }
      : null,
  ].filter(Boolean);

  const clearFilterChip = (chipKey) => {
    if (chipKey === "subjectObjectClass") setSubjectObjectClass("");
    if (chipKey === "auditMetaObjectClass") setAuditMetaObjectClass("");
    if (chipKey === "filterAction") setFilterAction("");
    if (chipKey === "filterDateFrom") setFilterDateFrom("");
    if (chipKey === "filterDateTo") setFilterDateTo("");
    if (chipKey === "showObjectId") setShowObjectId(false);
    if (chipKey === "sortDirection") setSortDirection("desc");
  };

  const clearAllAuditsFilters = () => {
    setSubjectObjectClass("");
    setAuditMetaObjectClass("");
    setFilterAction("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setShowObjectId(false);
    setSortDirection("desc");
  };

  const compactTop = messageMode || embedded;
  const modalToolbarChips = activeFilterChips.map((chip) => ({
    ...chip,
    id: chip.key,
  }));
  const embeddedFilterLabel =
    "block text-sm text-gray-700 mb-1";
  const embeddedFilterSelect =
    "w-full px-2 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-sidebar-active/20";
  const defaultFilterLabel =
    "block text-sm font-semibold text-gray-700 mb-2";
  const defaultFilterSelect =
    "w-full border border-gray-300 rounded-xl px-4 py-2";

  const chips = buildAdminAuditsFilterChips({
    subjectObjectClass,
    auditMetaObjectClass,
    filterAction,
    filterDateFrom,
    filterDateTo,
    showObjectId,
    sortDirection,
    messageMode,
  });

  return (
    <div
      className={[
        messageMode ? "space-y-3" : embedded ? "space-y-4" : "space-y-6",
        embedded ? "transition-all duration-300" : "",
        embedded && globalList ? filterPanel.contentOffsetClass : (embedded && showFilterPanel ? "mr-[400px]" : ""),
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={
          messageMode
            ? "bg-white rounded-none shadow-none border-0 p-0"
            : embedded
              ? "bg-transparent rounded-none shadow-none border-0 p-0"
              : "bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        }
      >
        {embedded && globalList ? (
          <AtlasListToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search audits..."
            onFilterClick={filterPanel.toggle}
            filterOpen={filterPanel.isOpen}
            onRefresh={loadAudits}
            refreshDisabled={loading}
            refreshSpinning={loading}
            resultsMeta={{
              startRow: fromRow,
              endRow: toRow,
              totalCount: total,
              pageSize: limit,
              pageSizeOptions: LIMIT_OPTIONS,
              onPageSizeChange: (size) => {
                setLimit(size);
                setPage(1);
              },
              sortBy,
              sortOrder: sortDirection,
              sortOptions: AUDIT_SORT_OPTIONS,
              onSortByChange: () => {},
              onSortOrderToggle: () =>
                setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
            }}
            chipBar={
              chips.length > 0 ? (
                <AtlasFilterChipBar
                  chips={chips}
                  onChipClick={(chip) => {
                    if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                    else filterPanel.open();
                  }}
                  onRemove={(chip) => clearFilterChip(chip.key)}
                  onClearAll={clearAllAuditsFilters}
                />
              ) : null
            }
          />
        ) : compactTop ? (
          embedded ? (
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search"
                    className="pl-8 pr-3 py-1.5 border border-gray-400 rounded-xl bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active w-56"
                  />
                </div>
                <div className="flex items-center gap-0">
                  <button
                    type="button"
                    onClick={() => setShowFilterPanel((prev) => !prev)}
                    className="p-2 hover:bg-gray-50 rounded"
                    title={showFilterPanel ? "Close filters" : "Filter"}
                    aria-expanded={showFilterPanel}
                  >
                    <FaSlidersH className="h-5 w-5" />
                  </button>
                  <AtlasRefreshIconButton
                    onClick={loadAudits}
                    spinning={loading}
                    useToolbarButton={false}
                    buttonClassName="p-2 hover:bg-gray-50 rounded"
                    iconClassName="h-5 w-5"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4">
              <div className="flex items-center gap-3">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search"
                  className="flex-1 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active"
                />
                <button
                  type="button"
                  onClick={() => setShowFilterPanel((prev) => !prev)}
                  className="h-9 w-9 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center hover:bg-gray-100 shrink-0"
                  title={showFilterPanel ? "Close filters" : "Filter"}
                  aria-expanded={showFilterPanel}
                >
                  <FaSlidersH className="h-4 w-4 text-gray-600" />
                </button>
                <AtlasRefreshIconButton
                  onClick={loadAudits}
                  spinning={loading}
                  useToolbarButton={false}
                  buttonClassName="px-2.5 py-2 border border-gray-200 rounded hover:bg-gray-50 shrink-0"
                  iconClassName="h-4 w-4 text-gray-700"
                />
              </div>
            </div>
          )
        ) : (
          <>
            <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-sidebar-active" />
              {title}
            </h3>
            <AtlasListToolbar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search audits..."
              onFilterClick={() => setShowFilterPanel((prev) => !prev)}
              filterOpen={showFilterPanel}
              onRefresh={loadAudits}
              refreshDisabled={loading}
              refreshSpinning={loading}
              resultsMeta={{
                startRow: fromRow,
                endRow: toRow,
                totalCount: total,
                pageSize: limit,
                pageSizeOptions: LIMIT_OPTIONS,
                onPageSizeChange: (size) => {
                  setLimit(size);
                  setPage(1);
                },
                sortBy,
                sortOrder: sortDirection,
                sortOptions: AUDIT_SORT_OPTIONS,
                onSortByChange: () => {},
                onSortOrderToggle: () =>
                  setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
              }}
              chipBar={
                modalToolbarChips.length > 0 ? (
                  <AtlasFilterChipBar
                    chips={modalToolbarChips}
                    onChipClick={() => setShowFilterPanel(true)}
                    onRemove={(chip) => clearFilterChip(chip.key)}
                    onClearAll={clearAllAuditsFilters}
                  />
                ) : null
              }
            />
          </>
        )}

        {compactTop && !(embedded && globalList) ? (
          <>
            <div className="text-sm text-gray-600 mb-4 flex flex-wrap items-center gap-1">
              <span>Showing</span>
              <span className="relative" ref={limitMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowLimitMenu((prev) => !prev)}
                  className="text-red-600 hover:text-red-700 hover:underline font-semibold"
                >
                  {toRow - fromRow + (total > 0 ? 1 : 0)}
                </button>
                {showLimitMenu && (
                  <div className="absolute left-0 mt-1 w-24 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1">
                    {LIMIT_OPTIONS.map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setLimit(value);
                          setShowLimitMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )}
              </span>
              <span>results sorted by</span>
              <span className="text-red-600 font-semibold">Created</span>
              <span>,</span>
              <button
                type="button"
                onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
                className="text-red-600 hover:text-red-700 hover:underline font-semibold"
              >
                {sortDirection === "asc" ? "Ascending" : "Descending"}
              </button>
            </div>

            {activeFilterChips.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">Filters:</span>
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setShowFilterPanel(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-gray-200 text-gray-700 px-3 py-1 text-sm hover:bg-gray-300"
                  >
                    <span>{chip.label}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearFilterChip(chip.key);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          clearFilterChip(chip.key);
                        }
                      }}
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-400 text-white text-xs"
                    >
                      x
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : null}

        {searchTooShort && (
          <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 px-4 py-3 text-sm">
            Type at least {SEARCH_MIN_LENGTH} characters to search.
          </div>
        )}

        {!searchTooShort && searchTruncated && !loading && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
            This search matches a very large number of records, so only part of them are
            shown. Use a more specific search or add a date range.
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((n) => (
              <div key={n} className="animate-pulse rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="h-4 w-40 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 w-64 bg-gray-200 rounded mb-1"></div>
                <div className="h-3 w-56 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : audits.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
            No audits found
          </div>
        ) : (
          <div className="space-y-3">
            {audits.map((audit) => {
              const subject = audit.subjectObject || {};
              const actor = audit.actorAccount || {};
              const flatMessage = String(audit.message || audit.detail || "").trim();
              const actorName =
                actor.name ||
                audit.actor_name ||
                `${actor.firstName || ""} ${actor.lastName || ""}`.trim() ||
                "Unknown";
              const when = audit.date || audit.created_at || null;
              const grouped = groupMetas(audit);
              if (messageMode) {
                const lines = flatMessage.split("\n").filter(Boolean);
                const header = parseMessageHeader(flatMessage);
                const bodyLines = lines.length > 1 ? lines.slice(1) : lines;
                return (
                  <div key={audit.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <div className="bg-gray-100 px-4 py-3 flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-gray-800 break-words">{header.title}</div>
                        <div className="text-gray-600 text-sm mt-1 break-all">{header.subtitle}</div>
                      </div>
                      <div className="flex items-center gap-3 min-w-0">
                        <StaffAvatarCircle
                          accountId={actor?.id ?? actor?.account_id ?? actor?.accountId}
                          employee={actorEmployeeFromAuditActor(actor, actorName)}
                          name={actorName}
                          className="w-10 h-10 shrink-0"
                        />
                        <div className="text-right text-sm min-w-0">
                          <div className="text-gray-700 leading-5 break-words">{actorName}</div>
                          <div className="text-gray-500 leading-5">{formatRelativeTime(when)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-3 space-y-1">
                      {bodyLines.length > 0 ? (
                        bodyLines.map((line, idx) => (
                          <div key={`${audit.id}-line-${idx}`} className="text-sm leading-6 text-gray-800 break-words">
                            {line}
                          </div>
                        ))
                      ) : (
                        <div className="text-sm leading-6 text-gray-600">No details available.</div>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div key={audit.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                  <div className="bg-gray-100 px-4 py-3 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-gray-900">
                        {subject.className || "Record"}
                      </div>
                      <div className="text-gray-600 text-sm break-all">
                        {subject.name || "Unknown"}
                        {showObjectId && subject.id ? ` (${subject.id})` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 min-w-0">
                      <StaffAvatarCircle
                        accountId={actor?.id ?? actor?.account_id ?? actor?.accountId}
                        employee={actorEmployeeFromAuditActor(actor, actorName)}
                        name={actorName}
                        className="w-10 h-10 shrink-0"
                      />
                      <div className="text-right text-sm min-w-0">
                        <div className="text-gray-700 leading-5 break-words">{actorName}</div>
                        <div className="text-gray-500 leading-5">{formatShortDate(when)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-3">
                    {grouped.length === 0 ? (
                      <div className="text-sm text-gray-600">No details available.</div>
                    ) : (
                      grouped.map((block) => (
                        <div key={block.key}>
                          <div className="text-sm font-medium text-gray-700 mb-1 break-words">
                            {actionLabel(block.items?.[0]?.action)} {block.objectClassName || "Record"}
                            {block.objectName ? `: ${block.objectName}` : ""}
                            {showObjectId && block.objectId ? ` (${block.objectId})` : ""}
                          </div>
                          <ul className="list-disc pl-6 text-sm leading-6 text-gray-800 break-words">
                            {(() => {
                              const statusMeta = block.items.find((meta) => {
                                const name = String(meta.name || "").toLowerCase();
                                const property = String(meta.property || "").toLowerCase();
                                return name === "status" || property === "state";
                              });
                              const profileMeta = block.items.find(
                                (meta) =>
                                  String(meta.name || "").toLowerCase() === "profile",
                              );
                              const items = block.items.filter((meta) => meta !== statusMeta);

                              if (statusMeta && !profileMeta) {
                                items.unshift({
                                  id: `${block.key}-profile`,
                                  name: "profile",
                                  value: subject.name || "Unknown",
                                  statusValue: formatMetaValue(statusMeta),
                                });
                              }

                              return items.map((meta) => (
                                <li key={meta.id || `${meta.name}-${meta.property}-${meta.value}`}>
                                  {meta.name || meta.property || "Value"}: {formatMetaValue(meta)}
                                  {meta === profileMeta && statusMeta
                                    ? ` "${formatMetaValue(statusMeta)}"`
                                    : meta.statusValue
                                      ? ` "${meta.statusValue}"`
                                      : ""}
                                </li>
                              ));
                            })()}
                          </ul>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 text-sm text-muted-foreground pt-4 border-t border-gray-200 ${messageMode ? "hidden" : ""}`}>
          <span>
            Showing {fromRow} - {toRow} of {total} items
          </span>
          <div className="flex items-center gap-1 sm:gap-2 justify-between sm:justify-end w-full sm:w-auto">
            <button
              className="px-3 py-2 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>

            <div className="hidden md:flex items-center gap-1 sm:gap-2">
              {(() => {
                const visiblePages = [];
                if (totalPages <= 5) {
                  for (let i = 1; i <= totalPages; i++) {
                    visiblePages.push(i);
                  }
                } else {
                  if (page <= 3) {
                    visiblePages.push(1, 2, 3, '...', totalPages);
                  } else if (page >= totalPages - 2) {
                    visiblePages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    visiblePages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                  }
                }

                return visiblePages.map((p, idx) =>
                  p === '...' ? (
                    <span key={idx} className="px-3 py-2">...</span>
                  ) : (
                    <button
                      key={idx}
                      className={`px-3 py-2 border border-[#DC1F0033] rounded-md ${
                        page === p ? "bg-[#DC1F00] text-white" : "hover:bg-gray-100"
                      }`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
                );
              })()}
            </div>

            <span className="md:hidden px-2 text-xs">
              Page {page} of {totalPages}
            </span>

            <button
              className={`px-3 py-2 rounded-md ${
                page === totalPages 
                ? 'text-gray-400 border border-[#DC1F0033]' 
                : 'bg-buttonRed text-white hover:bg-buttonRed-hover'
              }`}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {embedded && globalList ? (
        <AtlasFilterPanel
          isOpen={filterPanel.isOpen}
          onClose={filterPanel.close}
          onApply={filterPanel.close}
          onClear={clearAllAuditsFilters}
        >
          <AdminAuditsFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            subjectObjectClass={subjectObjectClass}
            setSubjectObjectClass={setSubjectObjectClass}
            auditMetaObjectClass={auditMetaObjectClass}
            setAuditMetaObjectClass={setAuditMetaObjectClass}
            filterAction={filterAction}
            setFilterAction={setFilterAction}
            filterDateFrom={filterDateFrom}
            setFilterDateFrom={setFilterDateFrom}
            filterDateTo={filterDateTo}
            setFilterDateTo={setFilterDateTo}
            showObjectId={showObjectId}
            setShowObjectId={setShowObjectId}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            messageMode={messageMode}
          />
        </AtlasFilterPanel>
      ) : (
        <EmbeddedFilterPanel
          isOpen={showFilterPanel}
          onClose={() => setShowFilterPanel(false)}
          onClear={() => {
            setSubjectObjectClass("");
            setAuditMetaObjectClass("");
            setFilterAction("");
            setFilterDateFrom("");
            setFilterDateTo("");
            setShowObjectId(false);
          }}
          variant={embedded ? "embedded" : messageMode ? "message" : "floating"}
          stackZIndex={messageMode && !embedded ? 71 : embedded ? 50 : 70}
          showBackdrop={messageMode && !embedded}
          showHeaderIcon={!embedded}
          footerMode={
            embedded ? "embeddedButtons" : "legacyDoneClear"
          }
        >
              <div className="space-y-5">
                <div>
                  <label className={embedded ? EMBEDDED_FILTER_LABEL_CLASS : defaultFilterLabel}>
                    Subject Type
                  </label>
                  <select
                    value={subjectObjectClass}
                    onChange={(e) => setSubjectObjectClass(e.target.value)}
                    className={embedded ? EMBEDDED_FILTER_SELECT_CLASS : defaultFilterSelect}
                  >
                    {SUBJECT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={embedded ? EMBEDDED_FILTER_LABEL_CLASS : defaultFilterLabel}>
                    Detail Type
                  </label>
                  <select
                    value={auditMetaObjectClass}
                    onChange={(e) => setAuditMetaObjectClass(e.target.value)}
                    className={embedded ? EMBEDDED_FILTER_SELECT_CLASS : defaultFilterSelect}
                  >
                    {DETAIL_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={embedded ? EMBEDDED_FILTER_LABEL_CLASS : defaultFilterLabel}>
                    Action
                  </label>
                  <select
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                    className={embedded ? EMBEDDED_FILTER_SELECT_CLASS : defaultFilterSelect}
                  >
                    {ACTION_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {!messageMode && (
                  <div>
                    <label className={embedded ? EMBEDDED_FILTER_LABEL_CLASS : defaultFilterLabel}>
                      Date Range
                    </label>
                    <div className="flex gap-2">
                      <DateInput
                        value={filterDateFrom}
                        onChange={setFilterDateFrom}
                        className={
                          embedded
                            ? "w-full border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                            : "w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                        }
                      />
                      <DateInput
                        value={filterDateTo}
                        onChange={setFilterDateTo}
                        className={
                          embedded
                            ? "w-full border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                            : "w-full border border-gray-300 rounded-xl px-3 py-2 text-sm"
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    id="showObjectId"
                    type="checkbox"
                    checked={showObjectId}
                    onChange={(e) => setShowObjectId(e.target.checked)}
                    className="h-4 w-4 text-sidebar-active border-gray-300 rounded focus:ring-sidebar-active"
                  />
                  <label htmlFor="showObjectId" className="text-sm font-medium text-gray-700">
                    Show Object ID
                  </label>
                </div>

                <div>
                  <label className={embedded ? EMBEDDED_FILTER_LABEL_CLASS : defaultFilterLabel}>
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    disabled
                    className={
                      embedded
                        ? "w-full px-2 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 text-gray-700"
                        : "w-full border border-gray-300 rounded-xl px-4 py-2 bg-gray-100 text-gray-700"
                    }
                  >
                    <option value="created">Created</option>
                  </select>
                </div>

                <div>
                  <label className={embedded ? EMBEDDED_FILTER_LABEL_CLASS : defaultFilterLabel}>
                    Sort Direction
                  </label>
                  <select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value)}
                    className={embedded ? EMBEDDED_FILTER_SELECT_CLASS : defaultFilterSelect}
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
        </EmbeddedFilterPanel>
      )}
    </div>
  );
};

export default RichAuditsPanel;
